/**
 * 山口県の背景ツアー「山口めぐり」コンテンツ。
 *
 * 軸は一日の時間。夜明けの秋吉台から、夜の東後畑棚田へ。
 *
 * 秋吉台と秋芳洞について。
 *  鍾乳洞は岡山（満奇洞）ですでに使っている。
 *  だから山口では地表のカルスト台地を主題にし、秋芳洞は豆知識にとどめる。
 *  「日本三大カルスト」「日本最大級」は国・県の断定形が確認できない。使わない。
 *
 * 鳥居について。
 *  別府弁天池は別府厳島神社の境内、元乃隅神社は赤い鳥居が連なる景色。
 *  どちらも実際に鳥居があるので、鳥居を描かないという否定文を入れていない。
 *
 * ときわ公園について。
 *  園内に著作権のある作品に由来する大型モニュメントがある。
 *  絵にも文章にも出さない。英語記述で彫刻・像を明示的に排除している。
 *
 * 萩について。
 *  重伝建の町並みは他県で6か所すでに使っている（倉吉・若桜・所子・大森銀山・吹屋・倉敷）。
 *  だから町並みそのものではなく、白い土塀の上に夏みかんが張り出す小路を主題にする。
 *  萩反射炉は足場設置の案内が出ていた。絵と現地が食い違うので景色には使わない。
 *
 * そのほか調査で出てきた「書いてはいけないこと」。
 *  - 「日本三名橋」（錦帯橋）「日本三名塔」（瑠璃光寺五重塔）は国・県の断定形が確認できない。
 *  - 錦帯橋の入橋料、瑠璃光寺の保存修理の状況、角島大橋の開通年（未確認）は書かない。
 *  - 元乃隅神社の鳥居の上の賽銭箱への投げ入れは現在中止。書かない。
 *  - 阿武火山群は気象庁の活火山だが、噴火警戒レベルの数字は書かない。
 *  - 耳なし芳一（赤間神宮）は近代の文学作品と結びつく。扱わない。
 */

import type { StageTour } from '../types';

export const yamaguchiSceneryTour: StageTour = {
  scenes: [
    {
      id: 'akiyoshidai',
      name: '秋吉台',
      area: '美祢市・夜明け',
      image: '/assets/scenery/yamaguchi/scenes/01_akiyoshidai.webp',
      hotspots: [
        {
          id: 'akiyoshidai-shitei',
          x: 0.46,
          y: 0.42,
          title: '国定公園と特別天然記念物',
          body: '秋吉台は1955年に秋吉台国定公園に指定され、1964年には特別天然記念物にも指定されました。台地では例年2月の第3日曜日に山焼きが行われます。',
          source: 'https://karusuto.com/spot/akiyoshidai/',
          mapQuery: '秋吉台 カルスト展望台',
        },
        {
          id: 'akiyoshidai-sekkaigan',
          x: 0.74,
          y: 0.34,
          title: '雨が石灰岩を溶かしてつくった草原',
          body: '見わたすかぎりの草原に白い石灰岩が顔を出す地形は、雨水や地下水が石灰岩を溶かしてできたものです。台地にはおよそ570種の草原の植物が育ち、季節ごとに違う花が現れます。',
          source: 'https://yamaguchi-tourism.jp/spot/detail_14537.html',
          mapQuery: '秋吉台',
        },
        {
          id: 'akiyoshidai-akiyoshido',
          x: 0.22,
          y: 0.5,
          title: '台地の地下にのびる鍾乳洞',
          body: '台地の地下には鍾乳洞の秋芳洞が広がっています。総延長は10キロメートルを超え、こちらも特別天然記念物に指定されています。洞内は四季を通じて17度に保たれ、観光コースはおよそ1キロメートルです。',
          source: 'https://yamaguchi-tourism.jp/spot/detail_14475.html',
          mapQuery: '秋芳洞',
        },
      ],
    },
    {
      id: 'beppu-benten',
      name: '別府弁天池',
      area: '美祢市秋芳町・朝',
      image: '/assets/scenery/yamaguchi/scenes/02_beppu_benten.webp',
      hotspots: [
        {
          id: 'benten-meisui',
          x: 0.46,
          y: 0.46,
          title: '名水百選に選ばれた湧き水',
          body: '別府弁天池は、昭和60年7月20日に環境庁から日本名水百選に選定された湧き水です。摂氏14度の透明度の高い水で、透き通ったブルーの水が不思議なほどの美しさを見せます。',
          source: 'https://karusuto.com/spot/beppubentenike/',
          mapQuery: '別府弁天池',
        },
        {
          id: 'benten-keidai',
          x: 0.74,
          y: 0.36,
          title: '神社の境内から湧く水',
          body: '池は別府厳島神社の境内にあります。土地の長者が夢のお告げで弁財天をまつったところ、水が湧き出したという言い伝えが残っています。湧いた水は田をうるおし、すぐそばの養鱒場にも引かれています。',
          source: 'https://karusuto.com/spot/beppubentenike/',
          mapQuery: '別府厳島神社 美祢市',
        },
        {
          id: 'benten-seikatsu',
          x: 0.22,
          y: 0.5,
          title: '地域の生活用水でもある',
          body: 'この池の水は地域の人たちの飲み水などの生活水でもあるため、手や足を池の水に浸ける行為は禁止されています。湧き出た水は灌漑や養鱒にも利用されています。',
          source: 'https://karusuto.com/spot/beppubentenike/',
          mapQuery: '別府弁天池 美祢市秋芳町',
        },
      ],
    },
    {
      id: 'hagi-natsumikan',
      name: '萩城下町 夏みかんの土塀',
      area: '萩市・初夏の午前',
      image: '/assets/scenery/yamaguchi/scenes/03_hagi_natsumikan.webp',
      hotspots: [
        {
          id: 'hagi-judenken',
          x: 0.46,
          y: 0.44,
          title: '武家町として選ばれた二地区',
          body: '萩市の堀内地区と平安古地区は、昭和51年9月4日に重要伝統的建造物群保存地区に選定されました。種別はいずれも武家町です。市内ではほかに港町の浜崎、宿場町の佐々並市も選定されています。',
          source:
            'https://www.bunka.go.jp/seisaku/bunkazai/shokai/hozonchiku/judenken_ichiran.html',
          mapQuery: '萩市堀内地区 伝統的建造物群保存地区',
        },
        {
          id: 'hagi-shizoku',
          x: 0.74,
          y: 0.34,
          title: '夏みかんは士族を救うために',
          body: '萩市の公式ページによれば、夏みかんの経済栽培は明治維新後に生活に困った士族を救済するために始まり、主に堀内と平安古の地区で行われてきました。萩市は、この経済栽培が150周年を迎えたことを紹介しています。',
          source: 'https://www.city.hagi.lg.jp/soshiki/55/h69169.html',
          mapQuery: '萩 平安古 夏みかん',
        },
        {
          id: 'hagi-sekaiisan',
          x: 0.22,
          y: 0.5,
          title: '城下町そのものが世界遺産',
          body: '世界遺産の構成資産としての萩城下町は、萩城跡の中堀の内側、旧上級武家地、旧町人地の三つの地区からなります。旧上級武家地や旧町人地には、江戸時代の町割りや上級武家の屋敷、石垣などが良好な状態で残っています。',
          source: 'https://www.city.hagi.lg.jp/site/sekaiisan/h6080.html',
          mapQuery: '萩城下町',
        },
      ],
    },
    {
      id: 'kintaikyo',
      name: '錦帯橋',
      area: '岩国市・昼前',
      image: '/assets/scenery/yamaguchi/scenes/04_kintaikyo.webp',
      hotspots: [
        {
          id: 'kintaikyo-ryushitsu',
          x: 0.46,
          y: 0.44,
          title: '流された経験から生まれた橋',
          body: '錦帯橋は延宝元年、1673年に架けられました。しかし翌年の洪水で流失し、すぐに架け直されています。その後も1950年に流失し、1953年に再建されました。錦川の洪水とのつきあいが、この橋の形をつくってきました。',
          source: 'https://kankou.iwakuni-city.net/kintaikyo.html',
          mapQuery: '錦帯橋',
        },
        {
          id: 'kintaikyo-kugi',
          x: 0.74,
          y: 0.36,
          title: '巻きガネとカスガイの木組み',
          body: '橋の長さはおよそ193メートル、幅はおよそ5メートルです。巻きガネとカスガイを使った木組みの技法で造られています。木造部分の掛け替えは、江戸時代からほぼ橋大工の代替わりに合わせて行われてきました。',
          source: 'https://kankou.iwakuni-city.net/kintaikyo.html',
          mapQuery: '錦帯橋 岩国市横山',
        },
        {
          id: 'kintaikyo-gorenn',
          x: 0.22,
          y: 0.5,
          title: '五つのアーチと国の名勝',
          body: '錦帯橋は清流錦川に架かる五連のアーチの木造橋で、国の名勝に指定されています。長さは193.3メートル、幅は5メートル。屋形舟に揺られながら、水面からアーチを見上げる楽しみ方もあります。',
          source: 'https://yamaguchi-tourism.jp/spot/detail_11342.html',
          mapQuery: '錦帯橋 錦川',
        },
      ],
    },
    {
      id: 'omijima',
      name: '青海島',
      area: '長門市・昼',
      image: '/assets/scenery/yamaguchi/scenes/05_omijima.webp',
      hotspots: [
        {
          id: 'omijima-meisho',
          x: 0.46,
          y: 0.42,
          title: '海上アルプスと呼ばれる島',
          body: '青海島は北長門海岸国定公園に含まれ、国の名勝および天然記念物に指定されています。周囲はおよそ40キロメートル。切り立った姿から海上アルプスとも呼ばれ、洞門や断崖絶壁、石柱など数多くの奇岩が連なります。',
          source: 'https://nanavi.jp/sightseeing/omijima/',
          mapQuery: '青海島',
        },
        {
          id: 'omijima-futatsu',
          x: 0.74,
          y: 0.34,
          title: '歩いて見る道と海から見る道',
          body: '青海島の景色には二つの楽しみ方があります。陸からは青海島自然研究路を散策し、海上からは青海島観光遊覧船に乗って、洞門や断崖絶壁、奇岩を眺められます。',
          source: 'https://nanavi.jp/sightseeing/omijima/',
          mapQuery: '青海島自然研究路',
        },
        {
          id: 'omijima-domon',
          x: 0.22,
          y: 0.5,
          title: '洞門の中まで入る遊覧船',
          body: '仙崎から出る観光遊覧船は、一周におよそ1時間20分かかります。海の状態がよければ、岩に開いた洞門の中まで船が入っていきます。エメラルドグリーンの海と、そこに落ちこむ断崖を間近に見ることができます。',
          source: 'https://yamaguchi-tourism.jp/spot/detail_13625.html',
          mapQuery: '青海島観光汽船 仙崎',
        },
      ],
    },
    {
      id: 'tsunoshima-ohashi',
      name: '角島大橋',
      area: '下関市豊北町・夏の昼過ぎ',
      image: '/assets/scenery/yamaguchi/scenes/06_tsunoshima_ohashi.webp',
      hotspots: [
        {
          id: 'tsunoshima-1780m',
          x: 0.46,
          y: 0.44,
          title: '全長1780メートル',
          body: '角島大橋は下関市豊北町から角島へ渡る全長1780メートルの橋で、通行は無料です。橋の下には、南国リゾートのような白い砂浜とコバルトブルーの海が広がります。',
          source: 'https://yamaguchi-tourism.jp/spot/detail_11030.html',
          mapQuery: '角島大橋',
        },
        {
          id: 'tsunoshima-contrast',
          x: 0.74,
          y: 0.36,
          title: '青と白と緑のコントラスト',
          body: '晴れた夏の日には、海の青と道路の白、島の緑のコントラストが美しく、絶好のフォトスポットになります。海風を感じながら、サイクリングや散歩を楽しむ人もいます。',
          source: 'https://yamaguchi-tourism.jp/spot/detail_11030.html',
          mapQuery: '角島大橋 展望台',
        },
        {
          id: 'tsunoshima-ohama',
          x: 0.22,
          y: 0.5,
          title: '渡った先の白い砂浜',
          body: '角島は白い砂浜とコバルトブルーの海に囲まれた小さな島です。透明度の高い角島大浜海水浴場があり、釣りや海水浴、キャンプなど一年を通して海とのつきあいかたが用意されています。',
          source: 'https://yamaguchi-tourism.jp/spot/detail_10826.html',
          mapQuery: '角島 大浜海水浴場',
        },
      ],
    },
    {
      id: 'rurikoji',
      name: '瑠璃光寺五重塔',
      area: '山口市・午後',
      image: '/assets/scenery/yamaguchi/scenes/07_rurikoji.webp',
      hotspots: [
        {
          id: 'rurikoji-bodai',
          x: 0.46,
          y: 0.36,
          title: '兄の菩提を弔うための塔',
          body: '五重塔は国宝で、嘉吉2年（1442年）ごろに落慶しました。応永6年（1399年）に戦いで亡くなった大内氏25代の義弘の菩提を弔うため、弟である26代の盛見が造営したものです。',
          source: 'https://yamaguchi-city.jp/details/aa_ruri_tou.html',
          mapQuery: '瑠璃光寺五重塔',
        },
        {
          id: 'rurikoji-31m',
          x: 0.74,
          y: 0.52,
          title: '相輪の先まで31メートル',
          body: '高さは相輪の尖端まで31.2メートルあります。各層の軒が広く張り出し、屋根は檜皮葺きです。',
          source: 'https://yamaguchi-city.jp/details/aa_ruri_tou.html',
          mapQuery: '瑠璃光寺 山口市香山町',
        },
        {
          id: 'rurikoji-kozan',
          x: 0.22,
          y: 0.48,
          title: '塔を囲む香山公園の木々',
          body: '塔は香山公園の中に立っています。園内には桜がおよそ170本、梅がおよそ40本あり、秋には紅葉も色づきます。ひとまわり歩いておよそ90分ほどの広さです。',
          source: 'https://yamaguchi-tourism.jp/spot/detail_15401.html',
          mapQuery: '香山公園 山口市',
        },
      ],
    },
    {
      id: 'tokiwa-ko',
      name: 'ときわ公園 常盤湖',
      area: '宇部市・夕方前',
      image: '/assets/scenery/yamaguchi/scenes/08_tokiwa_ko.webp',
      hotspots: [
        {
          id: 'tokiwa-189ha',
          x: 0.46,
          y: 0.44,
          title: '湖を中心にした広い公園',
          body: 'ときわ公園は常盤湖を中心に、およそ189ヘクタールの敷地に広がる総合公園です。園内には、自然のままの動物の姿が見られるときわ動物園や、世界を旅する植物館があります。',
          source: 'https://yamaguchi-tourism.jp/spot/detail_10241.html',
          mapQuery: 'ときわ公園 宇部市',
        },
        {
          id: 'tokiwa-sakura',
          x: 0.74,
          y: 0.36,
          title: '桜3500本と季節の花',
          body: '園内には、およそ3500本の桜と、ショウブやツツジ、アジサイなどの花が植えられています。しょうぶ苑ではおよそ140品種・2万株のショウブが咲き、アジサイは5月下旬から6月下旬にかけて見ごろを迎えます。',
          source: 'https://yamaguchi-tourism.jp/spot/detail_10241.html',
          mapQuery: 'ときわ公園 常盤湖',
        },
        {
          id: 'tokiwa-chokoku',
          x: 0.22,
          y: 0.5,
          title: '1961年から続く彫刻展',
          body: 'ときわ公園の彫刻の丘では、1961年以来、大規模な野外彫刻の公募展「UBEビエンナーレ（現代日本彫刻展）」が2年に一度開かれています。',
          source: 'https://ube-kankou.or.jp/sightseeing/nature-park/post-17.html',
          mapQuery: 'ときわ公園 彫刻',
        },
      ],
    },
    {
      id: 'motonosumi',
      name: '元乃隅神社',
      area: '長門市油谷津黄・日没',
      image: '/assets/scenery/yamaguchi/scenes/09_motonosumi.webp',
      hotspots: [
        {
          id: 'motonosumi-byakko',
          x: 0.46,
          y: 0.42,
          title: '白狐のお告げで建てられた',
          body: '元乃隅神社は昭和30年に建立されました。地域の網元であった方の枕元に白狐が現れ、そのお告げによって建てられたと伝えられています。',
          source: 'https://nanavi.jp/sightseeing/motonosumiinarijinja/',
          mapQuery: '元乃隅神社',
        },
        {
          id: 'motonosumi-123',
          x: 0.74,
          y: 0.34,
          title: '十年かけて奉納された123基',
          body: '赤い鳥居は昭和62年から10年をかけて奉納されたもので、その数は123基。100メートル以上にわたって並ぶ景色が続きます。現在は鳥居の修繕工事のため、鳥居をくぐることはできません。',
          source: 'https://nanavi.jp/sightseeing/motonosumiinarijinja/',
          mapQuery: '元乃隅神社 鳥居',
        },
        {
          id: 'motonosumi-shiofuki',
          x: 0.22,
          y: 0.5,
          title: '赤い鳥居と青い海と緑の大地',
          body: '赤い鳥居、青い海、緑の大地の三つの色が重なるのが、この場所の景色です。近くの龍宮の潮吹は、白波が噴き上がる様子が天に昇る龍に似ていると言われています。',
          source: 'https://yamaguchi-tourism.jp/spot/detail_17060.html',
          mapQuery: '龍宮の潮吹き 長門市',
        },
      ],
    },
    {
      id: 'higashiushirobata',
      name: '東後畑棚田',
      area: '長門市油谷・夜',
      image: '/assets/scenery/yamaguchi/scenes/10_higashiushirobata.webp',
      hotspots: [
        {
          id: 'higashi-hyakusen',
          x: 0.46,
          y: 0.44,
          title: '日本の棚田百選のひとつ',
          body: '東後畑棚田は、1999年に農林水産省が発表した日本の棚田百選に、全国117市町村134地区の棚田のひとつとして選ばれました。長門市油谷地区には、およそ600ヘクタールの棚田地帯が広がっています。',
          source: 'https://nanavi.jp/sightseeing/tanada/',
          mapQuery: '東後畑棚田',
        },
        {
          id: 'higashi-nihonkai',
          x: 0.74,
          y: 0.36,
          title: '眼下に日本海が見える棚田',
          body: '棚田は通常、山間部にあることが多いのですが、ここは眼下に日本海を望むことができます。田に水を張るのは4月下旬から5月中旬ごろです。',
          source: 'https://nanavi.jp/sightseeing/tanada/',
          mapQuery: '東後畑棚田 展望',
        },
        {
          id: 'higashi-isaribi',
          x: 0.22,
          y: 0.5,
          title: '沖に並ぶ漁火',
          body: '5月中旬から8月上旬ごろにかけて、沖に漁火が並びます。近年は6月から漁火が増えはじめ、9月以降は漁場が沖に移っていくため、漁火は遠くなっていきます。',
          source: 'https://nanavi.jp/sightseeing/tanada/',
          mapQuery: '東後畑棚田 漁火',
        },
      ],
    },
  ],
};
